"use client"

import type React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Mail, MessageCircle, CheckCircle, Send, Clock, Loader2, ArrowRight, X, HelpCircle, ChevronDown } from "lucide-react"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { AnimatedBackground } from "@/components/ui/animated-background"
import Link from "next/link"
import { useToast, createToastHelpers } from "@/components/ui/toast"
import { analytics, trackEvent } from "@/lib/analytics"
import {
  CONTACT_SUBJECTS,
  CREATOR_PROGRAM_SUBJECT,
  type ContactSubject,
} from "@/lib/contact-subjects"

import { JsonLd } from "@/components/seo/json-ld"

interface FormData {
  name: string
  email: string
  subject: string
  message: string
}

interface FormErrors {
  name?: string
  email?: string
  subject?: string
  message?: string
}

export function ContactPageContent({
  initialSubject = "",
}: {
  initialSubject?: ContactSubject | ""
}) {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    subject: initialSubject,
    message: "",
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [ticketNumber, setTicketNumber] = useState<string>("")
  const isCreatorProgram = formData.subject === CREATOR_PROGRAM_SUBJECT

  const { addToast, removeToast } = useToast()
  const toast = createToastHelpers(addToast, removeToast)

  // Generate ContactPage structured data
  const generateContactPageSchema = () => {
    const contactPageSchema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact What Grass Is This?",
      "description": "Get in touch with our lawn care experts for personalized advice and support.",
      "url": "https://whatgrassisthis.com/contact",
      "mainEntity": {
        "@type": "Organization",
        "name": "What Grass Is This?",
        "url": "https://whatgrassisthis.com",
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "email": "support@whatgrassisthis.com",
            "availableLanguage": "English",
            "areaServed": "United States"
          },
          {
            "@type": "ContactPoint",
            "contactType": "technical support",
            "email": "support@whatgrassisthis.com",
            "availableLanguage": "English"
          }
        ]
      },
      "potentialAction": {
        "@type": "CommunicateAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://whatgrassisthis.com/contact",
          "inLanguage": "en-US",
          "actionPlatform": [
            "https://schema.org/WebSite"
          ]
        },
        "expectsAcceptanceOf": {
          "@type": "Offer",
          "category": "Contact Form"
        }
      },
      "publisher": {
        "@type": "Organization",
        "name": "What Grass Is This?",
        "url": "https://whatgrassisthis.com"
      }
    }
    return contactPageSchema
  }

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validateForm = () => {
    const newErrors: FormErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = "Name is required"
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters"
    } else if (formData.name.trim().length > 100) {
      newErrors.name = "Name must be less than 100 characters"
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address"
    }

    if (!formData.subject) {
      newErrors.subject = "Please select a subject"
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required"
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters"
    } else if (formData.message.trim().length > 1000) {
      newErrors.message = "Message must be less than 1000 characters"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/contact/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })

      const result = await response.json()

      if (response.ok && result.success) {
        setTicketNumber(result.ticket_number || "")
        trackEvent('contact_form_submitted', {
          subject: formData.subject || 'none',
          message_length: formData.message.length,
        })
        analytics.emailCaptured('contact_form')
        setIsSubmitting(false)
        setIsSubmitted(true)
      } else {
        throw new Error(result.error || 'Submission failed')
      }
    } catch (error: any) {
      setIsSubmitting(false)
      console.error('Contact submission error:', error)

      let errorMsg = "Failed to submit message. Please try again."

      // Handle specific error types
      if (error.message) {
        if (error.message.includes('rate limit') || error.message.includes('too many requests')) {
          errorMsg = "Too many requests. Please wait a few minutes before trying again."
        } else if (error.message.includes('validation') || error.message.includes('Invalid')) {
          errorMsg = error.message
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMsg = "Network error. Please check your connection and try again."
        }
      }

      toast.error("Submission Failed", errorMsg)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }))
    }
  }

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      subject: "",
      message: "",
    })
    setErrors({})
    setIsSubmitted(false)
  }

  // Glass control (input / select / textarea) classes, driven by the existing
  // per-field error / valid state. Mirrors the prototype's translucent fields.
  const controlClass = (err?: string, ok?: boolean) =>
    `w-full rounded-[15px] border-[1.5px] px-4 py-3.5 text-base shadow-sm backdrop-blur-sm transition-all duration-200 placeholder:text-gray-400 focus:outline-none focus:ring-4 ${
      err
        ? "border-red-300 bg-red-50/80 focus:border-red-500 focus:bg-red-50 focus:ring-red-200/50"
        : ok
        ? "border-green-300 bg-white/70 hover:border-green-400 focus:border-green-500 focus:bg-white/95 focus:ring-green-200/60"
        : "border-black/[0.12] bg-white/70 hover:border-green-300 focus:border-green-500 focus:bg-white/95 focus:ring-green-200/60"
    }`

  const primaryPill =
    "inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full bg-linear-to-r from-green-600 to-emerald-600 px-7 py-3.5 text-base font-bold text-white shadow-[0_12px_26px_-8px_rgba(22,163,74,0.5)] transition-all duration-200 hover:-translate-y-0.5 hover:from-green-700 hover:to-emerald-700 hover:shadow-[0_16px_32px_-8px_rgba(22,163,74,0.6)] focus:ring-4 focus:ring-green-200 sm:w-auto"
  const messagePlaceholder = isCreatorProgram
    ? "Paste the live post URL or a shareable video link, then add campaign topic, proof context, disclosure notes, and payout contact."
    : "Tell us how we can help you... (minimum 10 characters)"

  if (isSubmitted) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-linear-to-br from-green-50 via-white to-emerald-50">
        <AnimatedBackground />

        <div className="relative mx-auto w-full max-w-2xl px-4 py-8 md:px-6 lg:px-8">
          <section className="ds-glass animate-fade-in-up relative overflow-hidden rounded-[32px] p-7 text-center md:p-12">
            {/* Top accent stripe */}
            <div className="absolute left-0 right-0 top-0 h-1 bg-linear-to-r from-green-600 to-emerald-600" />

            {/* Success ring */}
            <div className="relative mx-auto mb-6 h-24 w-24">
              <div className="absolute inset-0 animate-ping rounded-full border-[3px] border-green-200 motion-reduce:hidden" />
              <div className="absolute inset-0 flex items-center justify-center rounded-full bg-linear-to-br from-green-50 to-emerald-100 shadow-[0_14px_30px_-10px_rgba(22,163,74,0.4)]">
                <CheckCircle className="h-12 w-12 text-green-700" />
              </div>
            </div>

            <h1
              className="font-editorial mb-3"
              style={{ color: "var(--ink)", fontSize: "clamp(1.7rem, 1.3rem + 1.4vw, 2.4rem)", lineHeight: 1.1 }}
            >
              {isCreatorProgram ? "Creator Submission Received" : "Message Sent Successfully!"}
            </h1>
            <p className="mx-auto mb-7 max-w-[34ch] text-base leading-relaxed md:text-lg" style={{ color: "var(--ink-soft)" }}>
              {isCreatorProgram
                ? "We have your creator proof link and will review it manually before replying."
                : "We've received your message and will get back to you within 24 hours."}
            </p>

            {/* Fact rows — all real data already in state */}
            <div className="mx-auto mb-8 grid max-w-md gap-2.5 text-left">
              <div className="flex items-center gap-3 rounded-[14px] border border-green-200/60 bg-white/60 px-4 py-3 text-sm" style={{ color: "var(--ink-soft)" }}>
                <span className="h-[9px] w-[9px] flex-shrink-0 rounded-full bg-green-500 shadow-[0_0_0_4px_#f0fdf4]" />
                <span>Confirmation sent to <b className="font-bold text-green-700">{formData.email}</b></span>
              </div>
              <div className="flex items-center gap-3 rounded-[14px] border border-green-200/60 bg-white/60 px-4 py-3 text-sm" style={{ color: "var(--ink-soft)" }}>
                <span className="h-[9px] w-[9px] flex-shrink-0 rounded-full bg-green-500 shadow-[0_0_0_4px_#f0fdf4]" />
                <span>
                  {isCreatorProgram ? (
                    <>
                      Subject <b className="font-bold text-green-700">Creator Program</b>
                    </>
                  ) : (
                    <>
                      Response within <b className="font-bold text-green-700">24 hours</b>
                    </>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-3 rounded-[14px] border border-green-200/60 bg-white/60 px-4 py-3 text-sm" style={{ color: "var(--ink-soft)" }}>
                <span className="h-[9px] w-[9px] flex-shrink-0 rounded-full bg-green-500 shadow-[0_0_0_4px_#f0fdf4]" />
                <span>Ticket {ticketNumber && <b className="font-bold text-green-700">#{ticketNumber}</b>}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col items-center gap-4">
              <Button asChild className={primaryPill}>
                <Link
                  href={isCreatorProgram ? "/creators" : "/timeline"}
                  aria-label={isCreatorProgram ? "Return to Creator Program" : "View your care timeline"}
                >
                  {isCreatorProgram ? "Return to Creator Program" : "View Care Timeline"}
                </Link>
              </Button>
              <Link
                href="/"
                aria-label="Return to home page"
                className="inline-flex min-h-[44px] items-center text-sm font-semibold underline underline-offset-4 transition-colors"
                style={{ color: "var(--ink-mute)" }}
              >
                Return Home
              </Link>
            </div>
          </section>
        </div>
      </div>
    )
  }

  const nameOk = !errors.name && formData.name.trim().length > 0
  const emailOk = !errors.email && formData.email.trim().length > 0 && validateEmail(formData.email)
  const subjectOk = !errors.subject && !!formData.subject
  const messageOk = !errors.message && formData.message.trim().length >= 10

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* ContactPage Structured Data */}
      <JsonLd id="contact-page-schema" data={generateContactPageSchema()} />
      <AnimatedBackground />

      <div className="relative px-4 py-8 md:px-6 lg:px-8">
        <div className="mx-auto max-w-[46rem]">
          {/* Breadcrumb */}
          <div>
            <BreadcrumbComponent items={[{ label: "Home", href: "/" }, { label: "Contact Us" }]} />
          </div>

          {/* Hero */}
          <div className="mx-auto mb-10 max-w-[44rem] text-center md:mb-12">
            <div className="mb-5 flex justify-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-white/60 px-4 py-2 text-sm font-semibold text-green-700 shadow-sm backdrop-blur-sm">
                <MessageCircle className="h-4 w-4" />
                {isCreatorProgram ? "Creator program submissions" : "We're here to help"}
              </span>
            </div>
            <h1
              className="font-editorial mb-4"
              style={{ color: "var(--ink)", fontSize: "clamp(2.3rem, 1.5rem + 3.2vw, 3.9rem)", lineHeight: 1.04, letterSpacing: "-0.018em" }}
            >
              {isCreatorProgram ? "Submit Creator" : "Need Help or Have a"}{" "}
              <span className="relative whitespace-nowrap text-green-700">
                {isCreatorProgram ? "Proof" : "Question?"}
                <span className="absolute -bottom-0.5 left-0 right-0 h-[0.12em] rounded-full bg-green-200" />
              </span>
            </h1>
            <p className="mx-auto mb-6 max-w-[40ch] text-lg leading-relaxed md:text-xl" style={{ color: "var(--ink-soft)" }}>
              {isCreatorProgram
                ? "Send the live URL, video link, screenshots link, and campaign details for manual review."
                : "Send us a message and our lawn care team will get back to you as soon as possible."}
            </p>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2.5">
              {(isCreatorProgram
                ? ["Live post URL", "Video links accepted", "Manual approval"]
                : ["24-hour response time", "Expert support team", "100% satisfaction guarantee"]
              ).map((label) => (
                <span key={label} className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--ink-soft)" }}>
                  <span className="h-[7px] w-[7px] flex-shrink-0 rounded-full bg-green-500 shadow-[0_0_0_4px_#f0fdf4]" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* Form card */}
          <section className="ds-glass rounded-[30px] p-6 sm:p-8 md:p-10 lg:p-12">
            {/* Card head */}
            <div className="mb-7 text-center md:mb-8">
              <div className="mx-auto mb-4 flex h-[54px] w-[54px] items-center justify-center rounded-2xl bg-linear-to-br from-green-600 to-emerald-600 text-white shadow-[0_14px_28px_-10px_rgba(22,163,74,0.55)]">
                <Send className="h-6 w-6" />
              </div>
              <h2 className="font-editorial mb-1.5 text-2xl md:text-3xl" style={{ color: "var(--ink)" }}>
                {isCreatorProgram ? "Creator submission details" : "Get in Touch"}
              </h2>
              <p className="text-sm md:text-[15px]" style={{ color: "var(--ink-mute)" }}>
                {isCreatorProgram
                  ? "This is separate from brand partnerships. Send links to proof so we can review it."
                  : "Fill out the form and we'll reply within 24 hours."}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--ink)" }}>
                    Name <span className="text-red-600">*</span>
                    {nameOk && <CheckCircle className="h-4 w-4 text-green-700" />}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={handleChange}
                    autoComplete="name"
                    className={controlClass(errors.name, nameOk)}
                    required
                    aria-label="Enter your full name"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "name-error" : undefined}
                  />
                  {errors.name && (
                    <p id="name-error" className="flex items-center gap-1.5 text-xs font-medium text-red-600">
                      <X className="h-3.5 w-3.5 flex-shrink-0" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--ink)" }}>
                    Email <span className="text-red-600">*</span>
                    {emailOk && <CheckCircle className="h-4 w-4 text-green-700" />}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    className={controlClass(errors.email, emailOk)}
                    required
                    aria-label="Enter your email address"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {errors.email && (
                    <p id="email-error" className="flex items-center gap-1.5 text-xs font-medium text-red-600">
                      <X className="h-3.5 w-3.5 flex-shrink-0" />
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject" className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--ink)" }}>
                  Subject <span className="text-red-600">*</span>
                  {subjectOk && <CheckCircle className="h-4 w-4 text-green-700" />}
                </Label>
                <div className="group relative">
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`${controlClass(errors.subject, subjectOk)} cursor-pointer appearance-none pr-12`}
                    required
                    aria-label="Select message subject"
                    aria-invalid={!!errors.subject}
                    aria-describedby={errors.subject ? "subject-error" : undefined}
                    style={{ backgroundImage: "none" }}
                  >
                    <option value="" className="text-gray-500">Select a topic...</option>
                    {CONTACT_SUBJECTS.map((subject) => (
                      <option key={subject} value={subject} className="text-gray-900">
                        {subject}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 transition-transform duration-200 group-focus-within:rotate-180 group-focus-within:text-green-700 group-hover:text-green-700" />
                </div>
                {errors.subject && (
                  <p id="subject-error" className="flex items-center gap-1.5 text-xs font-medium text-red-600">
                    <X className="h-3.5 w-3.5 flex-shrink-0" />
                    {errors.subject}
                  </p>
                )}
                {isCreatorProgram && (
                  <div className="rounded-[16px] border border-green-200/80 bg-green-50/80 px-4 py-3 text-sm leading-6 text-green-950">
                    <div className="flex gap-3">
                      <HelpCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-700" />
                      <div>
                        <p className="font-bold">Creator Program, not Partnership</p>
                        <p className="mt-1 text-green-900/80">
                          For video proof, paste a live social URL or shareable file link from YouTube,
                          TikTok, Instagram, Google Drive, Dropbox, or iCloud. This form does not upload
                          video files yet.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--ink)" }}>
                  Message <span className="text-red-600">*</span>
                  {messageOk && <CheckCircle className="h-4 w-4 text-green-700" />}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder={messagePlaceholder}
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  maxLength={1000}
                  className={`${controlClass(errors.message, messageOk)} min-h-[152px] resize-y leading-relaxed`}
                  required
                  aria-label="Enter your message"
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? "message-error" : undefined}
                />
                {errors.message && (
                  <p id="message-error" className="flex items-center gap-1.5 text-xs font-medium text-red-600">
                    <X className="h-3.5 w-3.5 flex-shrink-0" />
                    {errors.message}
                  </p>
                )}
                <div className="flex min-h-[18px] items-center justify-between gap-3">
                  <span
                    className={`text-xs transition-colors duration-200 ${
                      formData.message.length > 900
                        ? "text-red-600"
                        : formData.message.length > 800
                        ? "text-amber-600"
                        : "text-gray-500"
                    }`}
                  >
                    {formData.message.length}/1000 characters
                  </span>
                  {messageOk && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
                      <CheckCircle className="h-3.5 w-3.5" />
                      Ready to send
                    </span>
                  )}
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className={`relative min-h-[56px] w-full overflow-hidden rounded-full px-6 py-4 text-base font-bold text-white transition-all duration-200 focus:ring-4 focus:ring-green-200 ${
                  isSubmitting
                    ? "cursor-not-allowed bg-gray-400 shadow-none"
                    : "bg-linear-to-r from-green-600 to-emerald-600 shadow-[0_12px_26px_-8px_rgba(22,163,74,0.5)] hover:-translate-y-0.5 hover:from-green-700 hover:to-emerald-700 hover:shadow-[0_16px_32px_-8px_rgba(22,163,74,0.6)]"
                }`}
                aria-label={isSubmitting ? "Sending message..." : "Send message"}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin motion-reduce:animate-none" />
                    Sending Message...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" />
                    Send Message
                  </>
                )}
              </Button>
            </form>

            {/* Other ways to reach us */}
            <div className="mt-9 border-t pt-7" style={{ borderColor: "rgba(20,50,30,0.1)" }}>
              <h3 className="font-editorial mb-5 text-center text-xl" style={{ color: "var(--ink)" }}>
                Other Ways to Reach Us
              </h3>
              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                <a
                  href="mailto:support@whatgrassisthis.com"
                  className="group flex items-center gap-3.5 rounded-[18px] border border-green-200/70 bg-green-50/55 p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_16px_32px_-18px_rgba(18,46,30,0.4)]"
                >
                  <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[13px] border border-green-200 bg-white text-green-700">
                    <Mail className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-mute)" }}>
                      Email support
                    </span>
                    <span className="mt-0.5 block break-words text-sm font-bold transition-colors group-hover:text-green-700" style={{ color: "var(--ink)" }}>
                      support@whatgrassisthis.com
                    </span>
                  </span>
                </a>

                <div className="flex items-center gap-3.5 rounded-[18px] border border-green-200/70 bg-green-50/55 p-4">
                  <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[13px] border border-green-200 bg-white text-green-700">
                    <Clock className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-mute)" }}>
                      Business hours
                    </span>
                    <span className="mt-0.5 block text-sm font-bold" style={{ color: "var(--ink)" }}>
                      Mon-Fri, 9am-5pm CT
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ CTA */}
          <section className="ds-glass mx-auto mt-6 rounded-[28px] p-7 text-center md:p-10">
            <div className="mx-auto mb-4 flex h-[50px] w-[50px] items-center justify-center rounded-full border border-green-200 bg-green-50 text-green-700">
              <HelpCircle className="h-6 w-6" />
            </div>
            <h3 className="font-editorial mb-2.5 text-2xl md:text-3xl" style={{ color: "var(--ink)" }}>
              Looking for Quick Answers?
            </h3>
            <p className="mx-auto mb-6 max-w-[42ch] text-[15px] leading-relaxed" style={{ color: "var(--ink-soft)" }}>
              Check out our most common questions, or start analyzing your lawn right away.
            </p>
            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild className={`group ${primaryPill}`}>
                <Link href="/" aria-label="Analyze your lawn">
                  Analyze My Lawn
                  <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button asChild className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full border-[1.5px] border-green-200 bg-white/85 px-7 py-3.5 text-base font-bold text-green-800 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-green-300 hover:bg-white focus:ring-4 focus:ring-green-200 sm:w-auto">
                <Link href="/faq" aria-label="View frequently asked questions">
                  <HelpCircle className="h-5 w-5" />
                  View FAQ
                </Link>
              </Button>
            </div>
          </section>
        </div>
      </div>

    </div>
  )
}
